import { AxisLabelsFormatterContextObject, Options, SeriesOptionsType, YAxisOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighStock from 'highcharts/highstock';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import { Currency, DemeterDataFrequency, DemeterFilterTimeSpan, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { EventActionsEnum, EventCategoriesEnum, EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import {
    areaRangeChartType,
    chartColors,
    ChartContext,
    ChartDisplayType,
    ChartOptionsDefinitions,
    defalutLineWidth,
    defaultChartAxisTickLabelStyle,
    defaultChartAxisTitleStyle,
    defaultChartOptions,
    defaultZoneAxis,
    dotDashStyle,
    featuredLineWidth,
    HighchartsPlot,
    IChartPriceDataSeries,
    IChartProps,
    lineChartType,
    longDashStyle,
    shortDashStyle,
} from '../ChartDefinitions';
import chartService from '../ChartService';
import { getTimestampMap } from '../FilterTimeSpans/FilterTimeSpans';
import { ChartStudyType, StudyToSeriesMap as studyToSeriesMap } from '../Studies/StudiesDefinitions';

export interface IPriceChartRawProps extends IChartProps {
    displayType?: ChartDisplayType;
    linesSeries: IChartPriceDataSeries[];
    useAverageLine?: boolean;
    dataFrequency: DemeterDataFrequency;
    currency?: Currency;
    unitOfMeasure?: UnitOfMeasure;
    hidePriceNavigator?: boolean;
    filterTimeSpan?: DemeterFilterTimeSpan;
    lineSeriesScreenPercent?: number;
    // This is true when chart navigator has the entire data set and the timespans from the props will move it
    // around instead of the parent filtering the data by time spans. Otherwise every time the time span changes,
    // the navigator will only show the filtered data and be fully highlighted.
    navigatorHasFullData?: boolean;
    displayDecimalPlacesMinimum?: number;
    displayDecimalPlacesMaximum?: number;
    studies?: ChartStudyType[];
    visibleIndexes?: number[];
    disableTooltip?: boolean;
}

type YAxisOptionExtended = YAxisOptions & { currency?: Currency; unitOfMeasure?: UnitOfMeasure };

const lineSeriesBufferPercent = 5;
const defaultTickAmount = 6;
const seriesIdPrefix = 'priceChartRaw';

const forecastDataIndexModifier = 1;
const forwardCurveDataIndexModifier = 2;
const errorRangeDataIndexModifier = 3;

const PriceChartRaw: React.FC<IPriceChartRawProps> = (props: IPriceChartRawProps) => {
    // Chart hooks.
    const chartReference = useRef<HighchartsReact.RefObject>();
    const [forecastDataOn, setForecastDataOn] = useState<boolean>(true);
    const [translations] = useLanguage();

    const [highchartOptions, setHighchartOptions] = useState<Options>(() => ({
        ...defaultChartOptions,
        xAxis: {
            ...defaultChartOptions.xAxis,
            crosshair: {
                snap: true,
                dashStyle: longDashStyle,
            },
        },
        yAxis: [],
        series: [],
        navigator: {
            ...defaultChartOptions.navigator,
            enabled: !props.hidePriceNavigator,
            maskFill: scssVariables.ruleSetTwoAreaColor1,
            series: {
                dataGrouping: {
                    enabled: false,
                },
            },
        },
    }));

    const linesSeries = useMemo(() => {
        if (!props.useAverageLine) {
            return props.linesSeries;
        }

        const newLinesSeries = [...props.linesSeries];

        const getAverageByDate = (series: IChartPriceDataSeries[]) => {
            const allLinesData = series.map((x) => x.data).flat();
            const arrayOfAll = allLinesData.map((x) => {
                const sameDateNumbers = allLinesData.filter((y) => y.asOfDate.getTime() === x.asOfDate.getTime());
                const sum = sameDateNumbers.reduce((accumulator, currentValue) => accumulator + currentValue.value, 0);
                const average = sum / sameDateNumbers.length;

                return {
                    asOfDate: x.asOfDate,
                    value: average,
                    isActualValue: x.isActualValue,
                };
            });

            const currentSet = new Set();
            const uniqueArray = arrayOfAll.filter((x) => {
                const time = x.asOfDate.getTime();
                if (currentSet.has(time)) {
                    return false;
                }

                currentSet.add(time);
                return true;
            });

            return uniqueArray;
        };

        newLinesSeries.push({
            label: `${translations.words.average}`,
            forecastLabel: `${translations.words.average} ${translations.words.forecast}`,
            isPrimaryLine: false,
            data: getAverageByDate(newLinesSeries).sort((a, b) => a.asOfDate.getTime() - b.asOfDate.getTime()),
        });

        return newLinesSeries;
    }, [props.useAverageLine, props.linesSeries]);

    const getDataSeriesDefinitions = useCallback(() => {
        const linesDataSeries = linesSeries.flatMap((lineSeries, index) => {
            const color = chartColors.lineChartColorsRuleSetOne[index % chartColors.lineChartColorsRuleSetOne.length];
            const primaryLine = (index === 0 && lineSeries.isPrimaryLine !== false) || lineSeries.isPrimaryLine;
            return [
                {
                    ...chartService.getDataSeriesBase(lineSeries, lineChartType, color),
                    id: `${seriesIdPrefix}${index}`,
                    yAxis: 0,
                    events: {
                        legendItemClick() {
                            handleLegendItemClicked(this as unknown as ChartContext);
                        },
                    },
                    zoneAxis: defaultZoneAxis,
                    zones: [
                        {
                            value: 0,
                        },
                        {
                            dashStyle: longDashStyle,
                        },
                    ],
                    showInNavigator: true,
                    lineWidth: primaryLine ? featuredLineWidth : defalutLineWidth,
                    turboThreshold: 0,
                    visible: props.visibleIndexes ? props.visibleIndexes.includes(index) : true,
                },
                // These forecast x-axis registrations are there just for the legend.
                chartService.getForecastDataSeriesBase(lineSeries, color, handleForecastClicked),
                {
                    name: lineSeries.forwardCurveLabel,
                    yAxis: 0,
                    marker: {
                        enabled: false,
                    },
                    events: {
                        legendItemClick() {
                            handleLegendItemClicked(this as unknown as ChartContext);
                        },
                    },
                    data: [] as HighchartsPlot[],
                    zoneAxis: defaultZoneAxis,
                    color,
                    dashStyle: dotDashStyle,
                    showInLegend: true,
                    showInNavigator: true,
                    visible: props.visibleIndexes ? props.visibleIndexes.includes(index) : true,
                },
                {
                    type: areaRangeChartType,
                    name: lineSeries.forecastRangeLabel,
                    yAxis: 0,
                    data: [] as HighchartsPlot[],
                    color,
                    fillOpacity: 0.2,
                    marker: {
                        enabled: false,
                    },
                    showInLegend: true,
                    visible: props.visibleIndexes ? props.visibleIndexes.includes(index) : true,
                    events: {
                        legendItemClick() {
                            handleLegendItemClicked(this as unknown as ChartContext);
                        },
                    },
                },
            ];
        });

        let studiesSeries: SeriesOptionsType[] = [];
        if (linesSeries.length === 1 && props.studies && props.studies.length > 0) {
            let colorIndex = linesDataSeries.length / 4 - 1;

            studiesSeries = props.studies
                .filter((x) => x !== ChartStudyType.ForecastErrorRange)
                .flatMap((study) => {
                    const seriesDefinitions = studyToSeriesMap[study];
                    if (!seriesDefinitions) {
                        return [];
                    }

                    return seriesDefinitions.map((x) => {
                        colorIndex += 1;
                        const color = chartColors.lineChartColorsRuleSetOne[colorIndex % chartColors.lineChartColorsRuleSetOne.length];
                        const name = x.name ?? (x.nameValueGetter && x.nameValueGetter());

                        return {
                            ...x,
                            name,
                            linkedTo: `${seriesIdPrefix}0`, // Always maps to the first line series.
                            color,
                        };
                    });
                });
        }

        return [...linesDataSeries, ...(studiesSeries as typeof linesDataSeries)];
    }, [linesSeries, props.hidePriceNavigator, forecastDataOn, props.studies]);

    const getYAxisOptions = useCallback(() => {
        let yAxisOptions: YAxisOptionExtended[] = [
            {
                title: {
                    text: '',
                    style: defaultChartAxisTitleStyle,
                },
                labels: {
                    style: defaultChartAxisTickLabelStyle,
                    formatter() {
                        const context = this as unknown as AxisLabelsFormatterContextObject;
                        return formattingService.formatNumber(context.value, { precision: 1 });
                    },
                },
                gridLineWidth: 1,
                crosshair: {
                    snap: true,
                    dashStyle: shortDashStyle,
                },
                currency: props.currency,
                unitOfMeasure: props.unitOfMeasure,
            },
        ];

        if (!linesSeries.some((x) => x.currency || x.unitOfMeasure)) {
            return yAxisOptions;
        }

        const currentAndUnitOfMeasuresMap: { [key: string]: { currency?: Currency; unitOfMeasure?: UnitOfMeasure; order: number } } = {};
        let order = 0;
        linesSeries.forEach((x) => {
            order += 1;

            currentAndUnitOfMeasuresMap[`${x.currency}/${x.unitOfMeasure}`] = {
                currency: x.currency,
                unitOfMeasure: x.unitOfMeasure,
                order,
            };
        });

        yAxisOptions = Object.values(currentAndUnitOfMeasuresMap)
            .sort((x) => x.order)
            .map((x, index) => ({
                ...yAxisOptions[0],
                title: { ...yAxisOptions[0].title },
                labels: { ...yAxisOptions[0].labels },
                crosshair: { ...(yAxisOptions[0].crosshair as HighStock.AxisCrosshairOptions) },
                startOnTick: true,
                endOnTick: false,
                tickAmount: defaultTickAmount,
                currency: x.currency,
                unitOfMeasure: x.unitOfMeasure,
                opposite: index === 1,
            }));

        return yAxisOptions;
    }, [linesSeries]);

    // Main useEffect to update chart when props or data changes.
    useEffect(() => {
        const dataSeries = getDataSeriesDefinitions();
        const yAxisOptions = getYAxisOptions();
        const newYAxis = [...yAxisOptions];
        const isTooltipEnabled = props.disableTooltip !== true;
        const today = new Date();
        const maximumValues = Array(newYAxis.length).fill(0);

        const downloadData = chartService.getDownloadData(linesSeries, props.dataFrequency);

        [...linesSeries].forEach((series, index) => {
            const lastActualValueDataIndex = series.data.findLastIndex((x) => x.isActualValue);
            const hasOnlyForecastData = series.data.length > 0 && lastActualValueDataIndex === -1;
            const currentDataSeriesIndex = index * 4;
            const hasErrorRanges = series.forecastErrorRangeData && series.forecastErrorRangeData.length > 0;
            let firstForecastDate = today;

            if (series.data.length > 0 && hasOnlyForecastData) {
                firstForecastDate = series.data[0] && series.data[0].asOfDate;
            } else if (series.data.length > lastActualValueDataIndex + 1) {
                firstForecastDate = series.data[lastActualValueDataIndex]?.asOfDate;
            }
            const endIndex = forecastDataOn ? series.data.length : lastActualValueDataIndex + 1;
            const currentDataSeriesItem = dataSeries[currentDataSeriesIndex] as ChartOptionsDefinitions;
            currentDataSeriesItem.type = (props.displayType ?? ChartDisplayType.Line).toLowerCase();
            currentDataSeriesItem.data = series.data
                .slice(0, endIndex)
                .map((item) => ({ x: item?.asOfDate.getTime(), y: item.value, isActualValue: item.isActualValue }));

            if (series.forwardCurveData && series.forwardCurveData.length > 0) {
                // Only let the forward curve define the actual/forecast boundary when the price series has no forecast portion
                // of its own. When the series already carries its own forecast values (actual rows followed by forecast rows),
                // keep the boundary at the last actual value so those actual rows are not rendered as forecast.
                const seriesHasActualAndForecastData = lastActualValueDataIndex >= 0 && lastActualValueDataIndex < series.data.length - 1;
                if (!seriesHasActualAndForecastData) {
                    firstForecastDate = series.forwardCurveData[0]?.asOfDate;
                }
                (dataSeries[currentDataSeriesIndex + forwardCurveDataIndexModifier] as ChartOptionsDefinitions).data = series.forwardCurveData?.map((item) => ({
                    x: item?.asOfDate.getTime(),
                    y: item.value,
                    isActualValue: item.isActualValue,
                }));

                if (series.forwardCurveLineStyle) {
                    (dataSeries[currentDataSeriesIndex + forwardCurveDataIndexModifier] as ChartOptionsDefinitions).dashStyle = series.forwardCurveLineStyle;
                }
            }

            if (hasErrorRanges && lastActualValueDataIndex >= 0) {
                const forecastData = series.data.slice(lastActualValueDataIndex + 1);
                const errorRangeData: number[][] = [];
                forecastData.forEach((dataPoint, forecastIndex) => {
                    const errorRange = series.forecastErrorRangeData![forecastIndex];
                    if (errorRange && dataPoint.value !== null) {
                        const lowerBound = errorRange.lowerErrorRangeValue;
                        const upperBound = errorRange.upperErrorRangeValue;
                        errorRangeData.push([dataPoint.asOfDate.getTime(), lowerBound, upperBound]);
                    }
                });

                if (errorRangeData.length > 0) {
                    (dataSeries[currentDataSeriesIndex + errorRangeDataIndexModifier] as ChartOptionsDefinitions).data = errorRangeData;
                } else {
                    (dataSeries[currentDataSeriesIndex + errorRangeDataIndexModifier] as ChartOptionsDefinitions).data = [];
                }
            }
            let yAxisIndex = 0;
            if (newYAxis.length > 1 && (series.currency || series.unitOfMeasure)) {
                yAxisIndex = newYAxis.findIndex((x) => x.currency === series.currency && x.unitOfMeasure === series.unitOfMeasure);
                dataSeries[currentDataSeriesIndex].yAxis = yAxisIndex;
                dataSeries[currentDataSeriesIndex + forwardCurveDataIndexModifier].yAxis = yAxisIndex;
                dataSeries[currentDataSeriesIndex + errorRangeDataIndexModifier].yAxis = yAxisIndex;
            } else {
                currentDataSeriesItem.yAxis = 0;
                dataSeries[currentDataSeriesIndex + forwardCurveDataIndexModifier].yAxis = 0;
            }

            maximumValues[yAxisIndex] = Math.max(maximumValues[yAxisIndex], ...currentDataSeriesItem.data!.map((x) => Math.abs(x.y ?? 0)));

            newYAxis[yAxisIndex].maxPadding = lineSeriesBufferPercent / 100.0;
            newYAxis[yAxisIndex].minPadding = lineSeriesBufferPercent / 100.0;

            // Push the point forward so it doesn't think the last actual is a forecast.
            // TODO: Do different math based on monthly/weekly data.
            currentDataSeriesItem.zones![0].value = (firstForecastDate?.getTime() ?? 0) + 1000;
            currentDataSeriesItem.dashStyle = hasOnlyForecastData ? longDashStyle : undefined;
            currentDataSeriesItem.showInLegend = series.data.length > 0;
            dataSeries[currentDataSeriesIndex + forecastDataIndexModifier].showInLegend =
                series.data.length > 0 && !hasOnlyForecastData && firstForecastDate !== today;
            dataSeries[currentDataSeriesIndex + forwardCurveDataIndexModifier].showInLegend =
                (dataSeries[currentDataSeriesIndex + forwardCurveDataIndexModifier] as ChartOptionsDefinitions).data!.length > 0;
            dataSeries[currentDataSeriesIndex + errorRangeDataIndexModifier].showInLegend =
                (dataSeries[currentDataSeriesIndex + errorRangeDataIndexModifier] as ChartOptionsDefinitions).data!.length > 0;
        });

        const lineMinimumValue = Math.min(
            ...linesSeries.map((x) => [...x.data.map((y) => y.value), ...(x.forecastErrorRangeData?.map((y) => y.lowerErrorRangeValue) ?? [])]).flat(),
        );

        const lineMaximumValue = Math.max(
            ...linesSeries.map((x) => [...x.data.map((y) => y.value), ...(x.forecastErrorRangeData?.map((y) => y.upperErrorRangeValue) ?? [])]).flat(),
        );

        (newYAxis as YAxisOptions[])[0].min = lineMinimumValue - (lineMinimumValue * lineSeriesBufferPercent) / 100.0;
        (newYAxis as YAxisOptions[])[0].max = lineMaximumValue + (lineMaximumValue * lineSeriesBufferPercent) / 100.0;

        const newOptions = {
            ...highchartOptions,
            ...{ series: dataSeries },
            ...{ yAxis: newYAxis },
            ...{
                xAxis: {
                    ...defaultChartOptions.xAxis,
                    crosshair: isTooltipEnabled && { snap: true, dashStyle: longDashStyle },
                },
            },
            ...{
                navigator: {
                    ...highchartOptions.navigator,
                    ...{
                        enabled: !props.hidePriceNavigator,
                    },
                },
            },
            ...{
                tooltip: {
                    enabled: isTooltipEnabled,
                    formatter() {
                        const context = this as unknown as ChartContext;

                        if (context.series.initialType === areaRangeChartType) {
                            return chartService.getAreaRangeTooltipText(context, {
                                nameOverride: !linesSeries.some((lineSeries) => lineSeries.forwardCurveLabel === newOptions.series[context.series.index].name)
                                    ? newOptions.series![context.series.index + forecastDataIndexModifier]?.name
                                    : '',
                                dataFrequency: props.dataFrequency,
                                displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum ?? 0,
                                displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum ?? 4,
                            });
                        }

                        return chartService.getTooltipText(context, {
                            nameOverride: !linesSeries.some((lineSeries) => lineSeries.forwardCurveLabel === newOptions.series[context.series.index].name)
                                ? newOptions.series![context.series.index + forecastDataIndexModifier]?.name
                                : '',
                            dataFrequency: props.dataFrequency,
                            displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum ?? 0,
                            displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum ?? 4,
                        });
                    },
                },
            },
            downloadData,
        };

        newOptions.yAxis.forEach((axis, index) => {
            axis.labels!.formatter = function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                const context = this as unknown as AxisLabelsFormatterContextObject;
                return formattingService.formatNumber(context.value, {
                    precision: formattingService.getDisplayDecimalPlacesMinimumForCharts(maximumValues[index] ?? 0),
                });
            };
            axis!.title!.text = chartService.getCurrencyAndUnitOfMeasureText(axis.unitOfMeasure, axis.currency);

            if (!isTooltipEnabled) {
                axis.crosshair = false;
            }
        });

        setHighchartOptions(newOptions as Options);
        handleFilterTimeSpanChange();
    }, [forecastDataOn, props.displayType, props.disableTooltip, linesSeries, props.lineSeriesScreenPercent, getDataSeriesDefinitions]);

    useEffect(() => {
        handleFilterTimeSpanChange();
    }, [props.filterTimeSpan]);

    // Unique price chart functions.
    const handleFilterTimeSpanChange = () => {
        if (!props.filterTimeSpan || linesSeries.length === 0 || linesSeries[0].data.length === 0) {
            return;
        }

        const oldestDate = Math.min(...linesSeries.map((x) => x.data[0]?.asOfDate.getTime()));
        const latestDataDate = Math.max(...linesSeries.map((x) => x.data[x.data.length - 1]?.asOfDate.getTime()));

        if (props.navigatorHasFullData) {
            const minimumDateForTimeSpan = getTimestampMap(new Date(latestDataDate))[props.filterTimeSpan as DemeterFilterTimeSpan];
            chartReference.current?.chart.xAxis[0].setExtremes(Math.max(minimumDateForTimeSpan, oldestDate), latestDataDate);
        } else {
            const latestForwardCurveDate = Math.max(
                ...linesSeries.map((x) => {
                    if (x.forwardCurveData && x.forwardCurveData.length > 0) {
                        return x.forwardCurveData![x.forwardCurveData!.length - 1].asOfDate.getTime();
                    }

                    return 0;
                }),
            );
            chartReference.current?.chart.xAxis[0].setExtremes(oldestDate, Math.max(latestDataDate, latestForwardCurveDate));
        }
    };

    const handleLegendItemClicked = (context: ChartContext) => {
        const seriesClicked = context.chart.series[context.index];

        loggingService.trackEventWithAnalytics(
            EventActionsEnum.ChartInteraction,
            EventCategoriesEnum.LegendItemClicked,
            seriesClicked.name,
            EventDataTargetsEnum.PriceGraph,
        );

        // If we clicked on a line series, also make the forward curve go away.
        if (context.index % 4 === 0) {
            if (seriesClicked.visible) {
                context.chart.series[context.index + forecastDataIndexModifier].hide();
                context.chart.series[context.index + forwardCurveDataIndexModifier].hide();
                context.chart.series[context.index + errorRangeDataIndexModifier].hide();
            } else {
                if (forecastDataOn) {
                    context.chart.series[context.index + forecastDataIndexModifier].show();
                }
                context.chart.series[context.index + forwardCurveDataIndexModifier].show();
                context.chart.series[context.index + errorRangeDataIndexModifier].show();
            }
        } else if (!seriesClicked.visible && !context.chart.series[context.index - errorRangeDataIndexModifier].visible) {
            // If we click on the forward curve while the lines series is gone, bring it back as well.
            context.chart.series[context.index - forwardCurveDataIndexModifier].show();
            if (forecastDataOn) {
                context.chart.series[context.index - forecastDataIndexModifier].show();
            }
        }
    };

    const handleForecastClicked = (context: ChartContext) => {
        const seriesClicked = context.chart.series[context.index];

        loggingService.trackEventWithAnalytics(
            EventActionsEnum.ChartInteraction,
            EventCategoriesEnum.LegendItemClicked,
            seriesClicked.name,
            EventDataTargetsEnum.PriceGraph,
        );
        context.chart.series.forEach((item, index) => {
            if (index % 4 !== 1 || index === context.index) {
                return;
            }

            if (forecastDataOn) {
                item.hide();
            } else {
                item.show();
            }
        });

        setForecastDataOn(!forecastDataOn);
    };

    return (
        <HighchartsReact
            ref={(newChartReference: HighchartsReact.RefObject) => {
                if (chartReference.current || !newChartReference) {
                    return;
                }

                chartReference.current = newChartReference;
                if (props.chartReference) {
                    props.chartReference(newChartReference);
                }
            }}
            highcharts={HighStock}
            options={highchartOptions}
            containerProps={{ style: { height: '100%' } }}
        />
    );
};

export default memo(PriceChartRaw);
